import React, {useEffect, useMemo, useState} from "react";
import { Button, Icon, Input, Select, Table, TableHeader, TableBody, TableRow, TableHeaderCell, TableCell } from "semantic-ui-react";
import {useAppDispatch, useAppSelector} from "../../../hooks/hooks";
import {useDebounce} from "../../../hooks/useDebounce";
import {deleteListener, getListenerInit, getListeners, IListener, setListenerClientId, setPageNumber /*, getClients*/} from "./ListenersCRUD.slice";
import { buildQueryString } from "../../../helpers/buildQueryString";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import CheckboxCms from "../../../components/CheckboxCms/CheckboxCms";
import styles from './ListenersCRUD.module.scss'
import ListenersCRUDForm from "./ListenersCRUD.form";
import AppPagination from "../../../components/Pagination/AppPagination";
import {changePageSize} from "../../Darts/Games/DartsGamesCRUD.slice";

const ListenersCRUD = () => {
    const dispatch = useAppDispatch()
    const {listeners, pagination, noContent, clientId, clients} = useAppSelector(state => state.ListenersCRUD)
    const cms = useAppSelector(state => state.Auth.cms);
    const [showListenerForm, setShowListenerForm] = useState(false);
    const [editedListener, setEditedListener] = useState<IListener | null>(null);
    const [searchWord, setSearchWord] = useState("");
    const [sortField, setSortField] = useState<string>('name');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');

    useEffect(() => {
        getInitData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getInitData = () => {
        //dispatch(getClients())
        dispatch(getListeners(buildQueryString({ pageNum: pagination.activePage, pageSize: pagination.pageSize })))
        dispatch(getListenerInit())
    }
    const generateButtons = (listener: IListener) => {
        if (cms['delete']) {
            return (
                <div className={styles.tableControls}>
                    <DeleteModal id={listener.id} titleModal={"Delete the listener"}
                        deleteAction={() => dispatch(deleteListener(listener.id))}
                        confirmText={"Are you sure you want to delete this listener?"}
                    />
                    <Icon name='pencil alternate' link size={"small"} onClick={() => onEditListener(listener)} />
                </div>
            );
        }
    };

    const onEditListener = (listener: IListener) => {
        setEditedListener(listener)
        setShowListenerForm(true)
    }

    const closeForm = () => {
        setShowListenerForm(false)
    }

    const onChangePager = (activePage: string | number | undefined) => {
        dispatch(setPageNumber(activePage))
        const params = {
            pageNum: activePage,
            pageSize: pagination.pageSize,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord,
            clientId
        }
        dispatch(getListeners(buildQueryString(params)))
    }

    const onSearch = () => {
        const params = {
            pageNum: 1,
            pageSize: pagination.pageSize,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord,
            clientId
        }
        dispatch(getListeners(buildQueryString(params)))
    }

    const handleSort = (field: string) => {
        const direction = sortField === field && sortDirection === 'ascending' ? 'descending' : 'ascending';
        setSortField(field);
        setSortDirection(direction);
        const params = {
            pageNum: pagination.activePage,
            pageSize: pagination.pageSize,
            sortField: field,
            sort: direction === "ascending" ? 0 : 1,
            search: searchWord,
            clientId
        }
        dispatch(getListeners(buildQueryString(params)))
    };

    const handleSelectClient = (client: any) => {
        dispatch(setListenerClientId(client))
        const params = {
            pageNum: 1,
            pageSize: pagination.pageSize,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord,
            clientId: client
        }
        dispatch(getListeners(buildQueryString(params)))
    }

    useDebounce(onSearch, searchWord, 500)

    const clientOptions = useMemo(() => {
        const options = clients.map(client => {
            return {key: client.id, value: client.id, text: client.name}
        })
        options.unshift({key: 0, value: 0, text: "All"})
        return options
    }, [clients]);

    const onChangePageSize = (value: any) => {
        dispatch(changePageSize(value))
        const params = {
            pageNum: pagination.activePage,
            pageSize: value,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord,
            clientId
        }
        dispatch(getListeners(buildQueryString(params)))
    }

    return (
        <div className="cms-container-general">
            <div className={styles.pageControls}>
                <div className={styles.filterInner}>
                    <Select
                        options={clientOptions}
                        value={clientId}
                        onChange={(e, {value}) => {
                            handleSelectClient(value)
                        }}
                        placeholder='Select client'
                    />
                </div>
                <Input
                    onChange={(e) => setSearchWord(e.target.value)}
                    icon='search'
                    placeholder='Search...'
                />
                {cms['create'] &&
                    <Button color="blue" onClick={() => setShowListenerForm(true)}>Add</Button>
                }
            </div>
            <div className="cms-table-general">
            <Table sortable celled textAlign="center">
                <TableHeader>
                    <TableRow>
                        {cms['id'] &&
                            <TableHeaderCell>Id</TableHeaderCell>
                        }
                        <TableHeaderCell sorted={sortField === 'name' ? sortDirection : undefined} onClick={() => handleSort('name')}>
                            Name
                        </TableHeaderCell>
                        <TableHeaderCell sorted={sortField === 'email' ? sortDirection : undefined} onClick={() => handleSort('email')} >
                            Email
                        </TableHeaderCell>
                        <TableHeaderCell>IP</TableHeaderCell>
                        <TableHeaderCell>Socket key</TableHeaderCell>
                        <TableHeaderCell>Active</TableHeaderCell>
                        <TableHeaderCell/>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {listeners?.map(listener => {
                        return (
                            <TableRow>
                                {cms['id'] &&
                                    <TableCell>{listener.id}</TableCell>
                                }
                                <TableCell>{listener.name}</TableCell>
                                <TableCell>{listener.email}</TableCell>
                                <TableCell>{listener.whiteIPs}</TableCell>
                                <TableCell>{listener.socketKey}</TableCell>
                                <TableCell><CheckboxCms value={listener.isActive} isReadyOnly={true}/></TableCell>
                                <TableCell>{generateButtons(listener)}</TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            </div>
            {showListenerForm &&
                <ListenersCRUDForm
                    editedListener={editedListener}
                    setEditedListener={setEditedListener}
                    closeForm={closeForm}
                />}
            {(noContent || listeners.length === 0) && <div className={styles.noContent}>There no listeners</div>}
            {listeners.length > 0 &&
                <AppPagination props={pagination} onChangePager={onChangePager} onChangePageSize={onChangePageSize} />
            }
        </div>
    );
}

export default ListenersCRUD;
